import React from 'react';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Filter, { DateRange, NumberRange } from '../../interfaces/filters';
import { Box, TextField } from '@mui/material';
import { textPattern } from '../../constants/patterns';
import DropDownValue from '../baseComponents/dropdownvalue';
import { DATE, DATE_RANGE, DROPDOWN, NUMBER, RANGE, TEXT } from '../../constants/constants';

const drawerWidth: number = 250;

interface FilterDrawerProps {
    filters: Filter[];
    setFilters: (filter: Filter[]) => void;
    drawerOpen: boolean;
    toggleDrawer: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({ filters = [], setFilters, drawerOpen, toggleDrawer}) => {
    const filterOptions = (options: any[], { inputValue }: { inputValue: string }, valueProp: string | undefined) => {
        const lower = inputValue.trim().toLowerCase();
        if (lower.length < 2) return [];         // require >= 2 chars
        const filtered = options.filter(opt => valueProp ? opt[valueProp].toLowerCase().includes(lower) : opt.toLowerCase().includes(lower));
        filtered.sort((a, b) => {
            const aStarts = valueProp ? a[valueProp].toLowerCase().startsWith(lower) : a.toLowerCase().startsWith(lower);
            const bStarts = valueProp ? b[valueProp].toLowerCase().startsWith(lower) : b.toLowerCase().startsWith(lower);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return valueProp ? a[valueProp].localeCompare(b) : a.localeCompare(b);
        });
        return filtered;
    };

    const handleFilterChange = (id: string, value: string | string[] | NumberRange | DateRange) => {
        let updatedFilterValues = [...filters];
        // Handle the value based on the filter's multiple property
        const filterObject = updatedFilterValues.find(val => val.id === id);
        if (filterObject) {
            filterObject.value = value;
        }
        setFilters(updatedFilterValues);
    };

    return (
        <Drawer
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
            variant="persistent"
            anchor="left"
            open={drawerOpen}
        >
            <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row' }}>
                <Typography variant="h6">
                    Filters
                </Typography>
                <IconButton onClick={toggleDrawer} aria-label="close drawer">
                    <CloseIcon />
                </IconButton>
            </Toolbar>
            <Box mb={3} border={1} borderColor="grey.300" p={2} borderRadius={2}>
                {filters.filter(
                    val => val.visibility === undefined || (val.visibility?.operation === "equals" && filters.find(value => value.key === val.visibility?.key)?.value === val.visibility?.value)
                ).map((filter, index) => {
                    if (filter.type === DROPDOWN) {
                        return (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <DropDownValue
                                        key={filter.key}
                                        label={filter.label}
                                        multiple={filter.multiple}
                                        filterOptions={filter.filterOptions ? filterOptions : undefined}
                                        selectedValue={filter.value}
                                        onSelection={(value) => handleFilterChange(filter.id, value)}
                                        items={filter.list || []}
                                        valueProp={filter.valueProp}
                                        displayProp={filter.displayProp}
                                        keyProp={filter.keyProp}
                                        disabled={filter.disabled}
                                        noOptionsText={filter.noOptionsText}
                                    />
                                </Box>
                            </Box>
                        );
                    } else if (filter.type === DATE) {
                        return (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <TextField
                                        type="date"
                                        onChange={e => handleFilterChange(filter.id, e.target.value)}
                                        fullWidth
                                        value={filter.value}
                                        label={filter.label}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Box>
                            </Box>
                        );
                    } else if (filter.type === TEXT) {
                        return (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <TextField
                                        margin='none'
                                        label={filter.key === 'l0_interaction' ? 'L0_interaction' : filter.label}
                                        value={filter.value}
                                        onChange={(e) => handleFilterChange(filter.id, e.target.value.replace(textPattern, ""))}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        );
                    } else if (filter.type === NUMBER) {
                        return (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                                <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <TextField
                                        margin='none'
                                        label={filter.label}
                                        type="number"
                                        value={filter.value}
                                        onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        );
                    } else if (filter.type === RANGE) {
                        return (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography sx={{ mb: 1 }} variant="body2">{filter.label}</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                    <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                        <TextField
                                            margin='none'
                                            label="Min"
                                            type="number"
                                            value={(filter.value as NumberRange).min}
                                            // This is to set min if value is there or remove min key if value is nothing
                                            onChange={(e) => handleFilterChange(filter.id, { ...Object(filter.value), min: e.target.value })}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box width={'100%'} sx={{ flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                        <TextField
                                            margin='none'
                                            label="Max"
                                            type="number"
                                            value={(filter.value as NumberRange).max}
                                            onChange={(e) => handleFilterChange(filter.id, { ...Object(filter.value), max: e.target.value })}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        );
                    } else if (filter.type === DATE_RANGE) {
                        return (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography sx={{ mb: 1 }} variant="body2">{filter.label}</Typography>
                                <Box width={'100%'} sx={{ mt: 1.5, flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <TextField
                                        margin='none'
                                        label="Start Date"
                                        type="date"
                                        value={(filter.value as DateRange).startDate}
                                        onChange={(e) => handleFilterChange(filter.id, { ...Object(filter.value), startDate: e.target.value })}
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Box>
                                <Box width={'100%'} sx={{ mt: 1.5, flex: '5 1 0%', pointerEvents: filter.disabled ? 'none' : 'auto', opacity: filter.disabled ? 0.6 : 1 }}>
                                    <TextField
                                        margin='none'
                                        label="End Date"
                                        type="date"
                                        value={(filter.value as DateRange).endDate}
                                        onChange={(e) => handleFilterChange(filter.id, { ...Object(filter.value), endDate: e.target.value })}
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                </Box>
                            </Box>
                        );
                    }
                })}
            </Box>
        </Drawer>
    );
};

export default FilterDrawer;